/**
 * AnalyticsDashboard – Executive role home screen
 *
 * Design:
 *  - Background: #FBFBFB
 *  - Cards: glassmorphism (rgba(255,255,255,0.55), backdrop-blur, border rgba(255,255,255,0.7), border-radius 20px)
 *  - Primary accent: #FF6414
 *  - Fonts: Plus Jakarta Sans (headlines), Manrope (body)
 *  - Interactive filter chips: clicking chart segments adds a filter chip and re-fetches
 *  - 1-hour API cache (handled in service layer)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BsSearch, BsPeople, BsCalendarCheck, BsGraphUp } from "react-icons/bs";
import { HiUserAdd } from "react-icons/hi";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import {
  fetchUserOverviewAnalytics,
  fetchAllUserDetails,
} from "../../../service/SubscriptionWellDhanService";

ChartJS.register(ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip, Legend);

// ─── Palette ────────────────────────────────────────────────────────────────
const PRIMARY = "#FF6414";
const PALETTE = [
  "#FF6414", "#6366F1", "#10B981", "#F59E0B",
  "#EC4899", "#3B82F6", "#A855F7", "#14B8A6",
  "#EF4444", "#84CC16", "#8B5CF6", "#06B6D4",
];

// Labels to suppress across all charts
const NOISE_LABELS = new Set([
  "none", "unknown", "not specified", "n/a", "other",
  "no injury", "no injuries", "not available",
]);
const isNoise = (label) =>
  NOISE_LABELS.has(String(label ?? "").trim().toLowerCase());

// ─── Helpers ─────────────────────────────────────────────────────────────────
const glassCard = {
  background: "rgba(255,255,255,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(255,255,255,0.85)",
  borderRadius: 18,
  boxShadow: "0 4px 24px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)",
};

/**
 * Normalize the various array/object shapes the API returns into a simple
 * { label: count } plain object for charting.
 * Zero-count entries and noisy labels (None, Unknown, Not Specified, …) are dropped
 * unless keepAll=true.
 */
const normalize = (raw, keepAll = false) => {
  if (!raw) return {};
  let pairs = [];
  if (!Array.isArray(raw)) {
    pairs = Object.entries(raw)
      .filter(([, v]) => typeof v !== "object")
      .map(([k, v]) => [k, Number(v)]);
  } else {
    pairs = raw.map((item) => {
      const labelKey = Object.keys(item).find((k) => k !== "count");
      return [item[labelKey] ?? "Unknown", Number(item.count ?? 0)];
    });
  }
  return pairs.reduce((acc, [label, count]) => {
    if (count > 0 && (keepAll || !isNoise(label))) acc[label] = count;
    return acc;
  }, {});
};

const mapToChartData = (obj = {}, palette = PALETTE) => {
  const labels = Object.keys(obj);
  const values = Object.values(obj).map(Number);
  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: palette.slice(0, labels.length),
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };
};

const barData = (obj = {}) => ({
  labels: Object.keys(obj),
  datasets: [
    {
      label: "Count",
      data: Object.values(obj).map(Number),
      backgroundColor: Object.keys(obj).map((_, i) =>
        i === 0 ? PRIMARY : `${PRIMARY}${Math.round(255 * (0.85 - i * 0.06)).toString(16).padStart(2, "0")}`
      ),
      borderRadius: 8,
      barThickness: 22,
      hoverBackgroundColor: PRIMARY,
    },
  ],
});

const doughnutOpts = (onClick) => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: "68%",
  animation: { duration: 900, easing: "easeOutQuart" },
  plugins: {
    legend: { position: "bottom", labels: { font: { family: "Manrope", size: 11 }, padding: 10, usePointStyle: true, pointStyleWidth: 8 } },
    tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` } },
  },
  onClick,
});

const barOpts = (onClick) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 800, easing: "easeOutQuart" },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(15,23,42,0.85)",
      titleFont: { family: "Plus Jakarta Sans", size: 12 },
      bodyFont: { family: "Manrope", size: 12 },
      callbacks: { label: (ctx) => ` ${ctx.parsed.y}` },
    },
  },
  scales: {
    x: { ticks: { font: { family: "Manrope", size: 10 }, color: "#64748B" }, grid: { display: false }, border: { display: false } },
    y: { ticks: { font: { family: "Manrope", size: 10 }, color: "#64748B" }, grid: { color: "rgba(226,232,240,0.6)" }, border: { display: false } },
  },
  onClick,
});

const lineOpts = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 900, easing: "easeOutQuart" },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(15,23,42,0.85)",
      titleFont: { family: "Plus Jakarta Sans", size: 12 },
      bodyFont: { family: "Manrope", size: 12 },
      callbacks: { label: (ctx) => ` ${ctx.parsed.y} joined` },
    },
  },
  scales: {
    x: {
      ticks: { font: { family: "Manrope", size: 10 }, color: "#64748B", maxTicksLimit: 8 },
      grid: { display: false },
      border: { display: false },
    },
    y: {
      ticks: { font: { family: "Manrope", size: 10 }, color: "#64748B" },
      grid: { color: "rgba(226,232,240,0.6)" },
      border: { display: false },
      beginAtZero: true,
    },
  },
};


const StatCard = ({ label, value, icon }) => (
  <div
    className="stat-card"
    style={{
      display: "flex", alignItems: "center", gap: 7,
      background: "#fff", border: "1px solid #E2E8F0",
      borderRadius: 999, padding: "7px 16px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      fontFamily: "Manrope, sans-serif",
      whiteSpace: "nowrap",
    }}>
    {icon && <span style={{ display: "flex", alignItems: "center" }}>{icon}</span>}
    <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 14, color: "#1E293B" }}>{value ?? "—"}</span>
    <span style={{ fontSize: 12, color: "#64748B" }}>{label}</span>
  </div>
);

const ChartCard = ({ title, children, height = 210, style: extraStyle, className = "", delay = 0 }) => (
  <div
    className={`chart-card ${className}`}
    style={{
      ...glassCard,
      padding: "16px 16px 12px",
      minWidth: 0,          /* prevent grid blowout */
      overflow: "hidden",
      ...extraStyle,
      animation: `ccFadeUp 0.5s ease ${delay}ms both`,
    }}
  >
    <div
      style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
        paddingBottom: 10, borderBottom: "1px solid rgba(226,232,240,0.5)",
      }}
    >
      <span
        style={{
          display: "inline-block", width: 3, height: 14, borderRadius: 99,
          background: PRIMARY, flexShrink: 0,
        }}
      />
      <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 12, color: "#334155", letterSpacing: "0.01em" }}>
        {title}
      </span>
    </div>
    <div style={{ height }}>{children}</div>
  </div>
);

const FilterChip = ({ label, onRemove }) => (
  <span
    style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: "rgba(99,102,241,0.10)", border: "1px solid rgba(99,102,241,0.25)",
      borderRadius: 99, padding: "5px 12px",
      fontFamily: "Manrope, sans-serif", fontSize: 12, color: "#6366F1", fontWeight: 600,
    }}
  >
    {label}
    <button
      onClick={onRemove}
      style={{ background: "none", border: "none", cursor: "pointer", color: "#6366F1", lineHeight: 1, padding: 0, fontSize: 15 }}
      title="Remove filter"
    >
      ×
    </button>
  </span>
);

const CoachCard = ({ coach, type }) => {
  const name = coach.coach_name || coach.name || "—";
  const assignedUsers = coach.assigned_users ?? coach.assigned_users_count ?? null;
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        ...glassCard,
        padding: "14px 16px",
        minWidth: 160,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 36, height: 36, borderRadius: "50%",
            background: type === "diet" ? "#10B981" : "#2563EB",
            color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 13,
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div>
          <div style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 600, fontSize: 13, color: "#1E293B" }}>
            {name}
          </div>
          <div
            style={{
              fontFamily: "Manrope, sans-serif", fontSize: 10,
              color: type === "diet" ? "#10B981" : "#2563EB",
              fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
            }}
          >
            {type === "diet" ? "Diet Coach" : "Fitness Coach"}
          </div>
        </div>
      </div>
      {assignedUsers != null && (
        <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 11, color: "#94A3B8" }}>
          {assignedUsers} assigned user{assignedUsers !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

const PlansExpiringSoonCard = ({ users = [], count }) => (
  <div
    style={{
      ...glassCard,
      padding: "16px 18px",
      minWidth: 0,
      overflow: "hidden",
      animation: "ccFadeUp 0.5s ease 200ms both",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid rgba(226,232,240,0.5)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-block", width: 3, height: 14, borderRadius: 99, background: "#F59E0B", flexShrink: 0 }} />
        <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 12, color: "#334155", letterSpacing: "0.01em" }}>
          Plans Expiring Soon
        </span>
      </div>
      {count != null && (
        <span style={{
          background: "rgba(245,158,11,0.12)", color: "#D97706",
          borderRadius: 99, padding: "2px 10px",
          fontFamily: "Manrope, sans-serif", fontWeight: 700, fontSize: 12,
        }}>
          {count}
        </span>
      )}
    </div>
    {users.length === 0 ? (
      <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 12, color: "#94A3B8", textAlign: "center", paddingTop: 8 }}>
        No plans expiring soon
      </div>
    ) : (
      <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        {users.map((u, i) => {
          const name = u.user_name || u.name || "—";
          const plan = u.plan_name || u.plan || "—";
          const expiry = u.expiry_date || u.expiry || null;
          const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
          const daysLeft = expiry
            ? Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24))
            : null;
          const urgent = daysLeft != null && daysLeft <= 3;
          return (
            <div
              key={i}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                background: urgent ? "rgba(245,158,11,0.06)" : "rgba(248,250,252,0.7)",
                border: `1px solid ${urgent ? "rgba(245,158,11,0.2)" : "rgba(226,232,240,0.5)"}`,
                borderRadius: 10, padding: "8px 12px",
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: urgent ? "#F59E0B" : "#CBD5E1",
                color: urgent ? "#fff" : "#64748B",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 11,
                flexShrink: 0,
              }}>
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 600, fontSize: 12, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {name}
                </div>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 10, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {plan}
                </div>
              </div>
              {expiry && (
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 10, color: urgent ? "#D97706" : "#94A3B8", fontWeight: 600 }}>
                    {daysLeft != null ? (daysLeft <= 0 ? "Expired" : `${daysLeft}d left`) : "—"}
                  </div>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 10, color: "#CBD5E1" }}>
                    {new Date(expiry).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const EVENT_TYPE_ID = "4850387a-8838-45d5-8c65-dce9d1f86de6";

export const AnalyticsDashboard = () => {
  const navigate = useNavigate();

  // ── Analytics data ────────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active filters – each is { type, value, label }
  const [filters, setFilters] = useState([]);
  const didMount = useRef(false);

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [usersData, setUsersData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const usersFetchedRef = useRef(false);

  // Fetch user list once for search. The ref keeps StrictMode's double-mount
  // from issuing the request twice.
  useEffect(() => {
    if (usersFetchedRef.current) return;
    usersFetchedRef.current = true;
    setSearchLoading(true);
    fetchAllUserDetails(EVENT_TYPE_ID)
      .then((res) => {
        if (res.success) setUsersData(res.data?.users || []);
      })
      .finally(() => setSearchLoading(false));
  }, []);

  // Live search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers([]);
      return;
    }
    const q = searchTerm.trim().toLowerCase();
    setFilteredUsers(
      usersData.filter((u) =>
        `${u.user_first_name} ${u.user_last_name}`.toLowerCase().includes(q)
      )
    );
  }, [searchTerm, usersData]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchTerm.trim().toLowerCase();
    const matches = usersData.filter((u) =>
      `${u.user_first_name} ${u.user_last_name}`.toLowerCase().includes(q)
    );
    if (matches.length === 1) navigate(`/dashboard/${matches[0].user_id}`);
  };

  // ── Analytics fetch ───────────────────────────────────────────────────────
  const buildFilterParams = useCallback((chips) => {
    const params = {};
    chips.forEach(({ type, value }) => {
      if (type === "gender") params.gender = value;
      if (type === "activity_level") params.activity_levels = [...(params.activity_levels || []), value];
      if (type === "dietary_preference") params.dietary_preferences = [...(params.dietary_preferences || []), value];
      if (type === "fitness_goal") params.fitness_goals = [...(params.fitness_goals || []), value];
      if (type === "location") params.locations = [...(params.locations || []), value];
    });
    return params;
  }, []);

  const loadData = useCallback(async (chips) => {
    setLoading(true);
    setError(null);
    const res = await fetchUserOverviewAnalytics(buildFilterParams(chips));
    if (res.success) setData(res.data);
    else setError(res.message || "Failed to load analytics");
    setLoading(false);
  }, [buildFilterParams]);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      loadData([]);
    }
  }, [loadData]);

  const addFilter = useCallback((type, value, label) => {
    setFilters((prev) => {
      if (prev.some((f) => f.type === type && f.value === value)) return prev;
      const next = [...prev, { type, value, label }];
      loadData(next);
      return next;
    });
  }, [loadData]);

  const removeFilter = useCallback((index) => {
    setFilters((prev) => {
      const next = prev.filter((_, i) => i !== index);
      loadData(next);
      return next;
    });
  }, [loadData]);

  const makeClickHandler = useCallback((type, labels) => (_evt, elements) => {
    if (!elements?.length) return;
    const value = labels[elements[0].index];
    if (value) addFilter(type, value, `${type.replace(/_/g, " ")}: ${value}`);
  }, [addFilter]);

  // ── Render states ─────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320, fontFamily: "Manrope, sans-serif", color: "#94A3B8" }}>
        Loading analytics…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 180, fontFamily: "Manrope, sans-serif", color: "#EF4444" }}>
        {error}
      </div>
    );
  }

  const d = data || {};

  // ── KPI values ────────────────────────────────────────────────────────────
  const genderObj    = normalize(d.gender_breakdown);
  const totalUsers   = Object.values(genderObj).reduce((a, b) => a + b, 0) || "—";
  const activeUsers  = (d.plan_stats?.users_with_active_diet_plan ?? 0)
                     + (d.plan_stats?.users_with_active_workout_plan ?? 0);
  const expiringSoon = d.plans_expiring_soon?.count ?? "—";
  const dietCoaches  = Array.isArray(d.coach_stats?.diet_coaches) ? d.coach_stats.diet_coaches : [];
  const fitCoaches   = Array.isArray(d.coach_stats?.fitness_coaches) ? d.coach_stats.fitness_coaches : [];
  const totalCoaches = dietCoaches.length + fitCoaches.length || "—";

  // Subscribed / Unsubscribed – check common field name variants
  const ss = d.subscription_stats || {};
  const subscribedCount   = ss.subscribed   ?? ss.total_subscribed   ?? ss.active_subscriptions   ?? null;
  const unsubscribedCount = ss.unsubscribed ?? ss.total_unsubscribed ?? ss.inactive_subscriptions ?? null;

  // ── Chart objects (all noise-filtered via normalize) ──────────────────────
  const activityObj  = normalize(d.activity_level_breakdown);
  const dietObj      = normalize(d.dietary_preference_breakdown);
  const goalObj      = normalize(d.fitness_goal_breakdown);
  const ageObj       = normalize(d.age_distribution);
  const locationObj  = normalize(d.location_distribution);
  const bmiObj       = normalize(d.bmi_distribution);
  const deviceObj    = normalize(d.device_os_breakdown, true);   // keep "Unknown OS" entries
  const loginObj     = normalize(d.login_type_breakdown);
  const brandObj     = normalize(d.brand_awareness_breakdown);
  const diseaseObj   = normalize(d.disease_breakdown);
  const injuryObj    = normalize(d.physical_injury_breakdown);

  const genderCD   = mapToChartData(genderObj);
  const activityCD = mapToChartData(activityObj);
  const dietCD     = mapToChartData(dietObj);
  const goalCD     = mapToChartData(goalObj);
  const ageCD      = barData(ageObj);
  const locationCD = barData(locationObj);
  const bmiCD      = barData(bmiObj);
  const deviceCD   = mapToChartData(deviceObj);
  const loginCD    = mapToChartData(loginObj);
  const brandCD    = mapToChartData(brandObj);
  const diseaseCD  = barData(diseaseObj);
  const injuryCD   = barData(injuryObj);

  // ── Joining trend data ────────────────────────────────────────────────────
  const joiningTrend  = Array.isArray(d.joining_stats?.daily_trend) ? d.joining_stats.daily_trend : [];
  const expiringUsers = Array.isArray(d.plans_expiring_soon?.users) ? d.plans_expiring_soon.users : [];
  const joiningTrendCD = joiningTrend.length > 0 ? {
    labels: joiningTrend.map((p) => {
      const date = new Date(p.date);
      return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    }),
    datasets: [{
      label: "New Users",
      data: joiningTrend.map((p) => p.count),
      borderColor: PRIMARY,
      backgroundColor: `${PRIMARY}22`,
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: PRIMARY,
      fill: true,
      tension: 0.4,
    }],
  } : null;

  const hasJoiningTrend    = joiningTrendCD !== null;
  const hasExpiringPlans   = expiringUsers.length > 0 || expiringSoon !== "—";
  const hasCoaches         = dietCoaches.length > 0 || fitCoaches.length > 0;

  return (
    <div style={{ background: "linear-gradient(135deg, #F0F4FF 0%, #FFF4EF 50%, #F0FFF4 100%)", minHeight: "100vh", padding: "20px 20px 36px", boxSizing: "border-box", overflowX: "hidden" }}>
      <style>{`
        @keyframes ccFadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .analytics-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-bottom: 14px;
          width: 100%;
          min-width: 0;
        }
        .analytics-grid .chart-card-2col { grid-column: span 2; }
        @media (max-width: 1099px) {
          .analytics-grid { grid-template-columns: repeat(2, 1fr); }
          .analytics-grid .chart-card-2col { grid-column: span 2; }
        }
        @media (max-width: 599px) {
          .analytics-grid { grid-template-columns: 1fr; gap: 10px; }
          .analytics-grid .chart-card-2col { grid-column: span 1; }
          .stat-card {
            padding: 5px 10px !important;
            font-size: 11px !important;
            white-space: normal !important;
            text-align: center;
            min-width: 0;
            flex-wrap: wrap;
            justify-content: center;
          }
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <h1 style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 20, color: "#1E293B", margin: 0 }}>
          Analytics Overview
        </h1>
        <p style={{ fontFamily: "Manrope, sans-serif", fontSize: 12, color: "#94A3B8", marginTop: 3, marginBottom: 0 }}>
          Click any chart segment to apply a filter · Data cached for 1 hour
          {loading && <span style={{ color: PRIMARY, marginLeft: 8 }}>Refreshing…</span>}
        </p>
      </div>

      {/* ── Search bar ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <div style={{ width: "100%", maxWidth: 480, position: "relative" }}>
          <form
            onSubmit={handleSearch}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#fff", borderRadius: 12,
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              padding: "8px 14px",
            }}
          >
            <BsSearch size={16} style={{ color: "#94A3B8", flexShrink: 0 }} />
            <input
              type="text"
              placeholder={searchLoading ? "Loading users…" : "Search users by full name..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1, border: "none", outline: "none", background: "transparent",
                fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#1E293B",
              }}
            />
            {searchTerm && (
              <button
                type="submit"
                style={{
                  background: PRIMARY, color: "#fff", border: "none", borderRadius: 8,
                  padding: "4px 12px", cursor: "pointer",
                  fontFamily: "Manrope, sans-serif", fontSize: 12, fontWeight: 600,
                }}
              >
                Go
              </button>
            )}
          </form>
          {filteredUsers.length > 0 && (
            <ul
              style={{
                position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12,
                boxShadow: "0 6px 20px rgba(0,0,0,0.09)", zIndex: 30,
                maxHeight: 200, overflowY: "auto", listStyle: "none",
                padding: "4px 0", margin: 0,
              }}
            >
              {filteredUsers.map((user) => (
                <li
                  key={user.user_id}
                  onClick={() => navigate(`/dashboard/${user.user_id}`)}
                  style={{
                    padding: "8px 14px", cursor: "pointer",
                    fontFamily: "Manrope, sans-serif", fontSize: 13, color: "#1E293B",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#FFF4EF")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {user.user_first_name} {user.user_last_name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Filter chips ────────────────────────────────────────────────────── */}
      {filters.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14, justifyContent: "center" }}>
          {filters.map((f, i) => (
            <FilterChip key={i} label={f.label} onRemove={() => removeFilter(i)} />
          ))}
          <button
            onClick={() => { setFilters([]); loadData([]); }}
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Manrope, sans-serif", fontSize: 12, color: "#94A3B8", textDecoration: "underline" }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* ── KPI chips ───────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 20 }}>
        <StatCard label="Total Users"         value={totalUsers}   icon={<BsPeople        size={14} style={{ color: PRIMARY }} />} />
        <StatCard label="Active Plan Users"   value={activeUsers}  icon={<BsCalendarCheck size={14} style={{ color: "#10B981" }} />} />
        <StatCard label="Plans Expiring Soon" value={expiringSoon} icon={<BsGraphUp       size={14} style={{ color: "#F59E0B" }} />} />
        <StatCard label="Total Coaches"       value={totalCoaches} icon={<BsPeople        size={14} style={{ color: "#2563EB" }} />} />
        {subscribedCount   != null && <StatCard label="Subscribed"        value={subscribedCount}   icon={<HiUserAdd size={14} style={{ color: "#10B981" }} />} />}
        {unsubscribedCount != null && <StatCard label="Unsubscribed"      value={unsubscribedCount} icon={<HiUserAdd size={14} style={{ color: "#94A3B8" }} />} />}
        {ss.revenue        != null && <StatCard label="Revenue"           value={`₹${Number(ss.revenue).toLocaleString()}`} icon={<BsGraphUp size={14} style={{ color: "#8B5CF6" }} />} />}
        {d.joining_stats?.this_month != null && (
          <StatCard label="Joined This Month" value={d.joining_stats.this_month} icon={<HiUserAdd size={14} style={{ color: "#64748B" }} />} />
        )}
      </div>

      {/* ── Daily Joining Trend – full width above charts ──────────────────── */}
      {hasJoiningTrend && (
        <div style={{ marginBottom: 14 }}>
          <ChartCard title="Daily Joining Trend" height={200} delay={0}>
            <Line data={joiningTrendCD} options={lineOpts} />
          </ChartCard>
        </div>
      )}

      {/* ── Charts – responsive grid: 4-col → 2-col → 1-col ── */}
      <div className="analytics-grid">

        {/* Row 1: four compact single-col charts */}
        <ChartCard title="Gender"    delay={0}  style={{ display: Object.keys(genderObj).length    === 0 ? "none" : undefined }}>
          <Doughnut data={genderCD}   options={doughnutOpts(makeClickHandler("gender",          genderCD.labels))} />
        </ChartCard>
        <ChartCard title="Activity" delay={60} style={{ display: Object.keys(activityObj).length === 0 ? "none" : undefined }}>
          <Doughnut data={activityCD} options={doughnutOpts(makeClickHandler("activity_level", activityCD.labels))} />
        </ChartCard>
        <ChartCard title="Age"      delay={120} style={{ display: Object.keys(ageObj).length      === 0 ? "none" : undefined }}>
          <Bar data={ageCD} options={barOpts(null)} />
        </ChartCard>
        <ChartCard title="BMI"      delay={180} style={{ display: Object.keys(bmiObj).length      === 0 ? "none" : undefined }}>
          <Bar data={bmiCD} options={barOpts(null)} />
        </ChartCard>

        {/* Plans Expiring Soon – sits at end of grid row 1 / start of row 2 */}
        {hasExpiringPlans && (
          <PlansExpiringSoonCard users={expiringUsers} count={expiringSoon !== "—" ? expiringSoon : undefined} />
        )}

        {/* Row 2: donut | donut | bar(2) */}
        <ChartCard title="Dietary Prefs" delay={60}  style={{ display: Object.keys(dietObj).length     === 0 ? "none" : undefined }}>
          <Doughnut data={dietCD}     options={doughnutOpts(makeClickHandler("dietary_preference", dietCD.labels))} />
        </ChartCard>
        <ChartCard title="Fitness Goals" delay={120} style={{ display: Object.keys(goalObj).length     === 0 ? "none" : undefined }}>
          <Doughnut data={goalCD}     options={doughnutOpts(makeClickHandler("fitness_goal",      goalCD.labels))} />
        </ChartCard>
        <ChartCard title="Location Distribution" height={190} delay={180} className="chart-card-2col" style={{ display: Object.keys(locationObj).length === 0 ? "none" : undefined }}>
          <Bar data={locationCD} options={barOpts(makeClickHandler("location", locationCD.labels))} />
        </ChartCard>

        {/* Row 3: donut | donut | bar(2) */}
        <ChartCard title="Device OS"     delay={0}  style={{ display: Object.keys(deviceObj).length   === 0 ? "none" : undefined }}>
          <Doughnut data={deviceCD}   options={doughnutOpts(null)} />
        </ChartCard>
        <ChartCard title="Login Types" delay={60} style={{ display: Object.keys(loginObj).length    === 0 ? "none" : undefined }}>
          <Doughnut data={loginCD}    options={doughnutOpts(null)} />
        </ChartCard>
        <ChartCard title="Health Conditions" height={190} delay={120} className="chart-card-2col" style={{ display: Object.keys(diseaseObj).length === 0 ? "none" : undefined }}>
          <Bar data={diseaseCD} options={barOpts(null)} />
        </ChartCard>

        {/* Row 4: donut | bar(2) */}
        <ChartCard title="Brand Awareness" delay={0} style={{ display: Object.keys(brandObj).length    === 0 ? "none" : undefined }}>
          <Doughnut data={brandCD}    options={doughnutOpts(null)} />
        </ChartCard>
        <ChartCard title="Physical Injuries" height={190} delay={60} className="chart-card-2col" style={{ display: Object.keys(injuryObj).length === 0 ? "none" : undefined }}>
          <Bar data={injuryCD} options={barOpts(null)} />
        </ChartCard>

      </div>

      {/* ── Coach Analytics ───────────────────────────────────────────────── */}
      {hasCoaches && (
        <div style={{ ...glassCard, padding: "16px 18px" }}>
          <div style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 14, color: "#1E293B", marginBottom: 14 }}>
            Coach Analytics
          </div>
          {dietCoaches.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 11, fontWeight: 700, color: "#10B981", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                Diet Coaches ({dietCoaches.length})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {dietCoaches.map((c, i) => <CoachCard key={i} coach={c} type="diet" />)}
              </div>
            </div>
          )}
          {fitCoaches.length > 0 && (
            <div>
              <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 11, fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                Fitness Coaches ({fitCoaches.length})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {fitCoaches.map((c, i) => <CoachCard key={i} coach={c} type="fitness" />)}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
